// Turnstile.tsx — renders the Cloudflare Turnstile widget when a site key is
// available. Turnstile is OPTIONAL in v1 (plan §7.4, addendum): if `siteKey` is
// null/empty (Turnstile not provisioned), this renders nothing and the form
// submits without a token — the BFF treats the token as optional in that mode.
//
// Loads the Cloudflare Turnstile script on demand (CSP already allowlists
// challenges.cloudflare.com per plan §10), renders the widget via the explicit
// `render` API, and reports the token up through `onToken` (null on expire/error).
import { useEffect, useRef } from "react";

// Minimal typing for the global injected by the Turnstile script.
interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
  "timeout-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
  language?: string;
  size?: "normal" | "compact" | "flexible";
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __zjlTurnstileOnLoad?: () => void;
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__zjlTurnstileOnLoad";
const SCRIPT_ID = "cf-turnstile-script";

/** Load the Turnstile script once; resolve when `window.turnstile` is ready. */
function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    const ready = () => resolve();
    window.__zjlTurnstileOnLoad = ready;

    if (existing) {
      if (window.turnstile) resolve();
      // else: wait for the onload callback set above.
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
}

export interface TurnstileProps {
  /** Site key from /config.json. Null/empty => widget is disabled (renders nothing). */
  siteKey: string | null;
  /** Called with the verification token, or null when it expires/errors. */
  onToken: (token: string | null) => void;
  /** Active locale, mapped to the widget's display language. */
  locale?: "zh" | "en";
  className?: string;
}

export default function Turnstile({ siteKey, onToken, locale = "zh", className }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest onToken without re-rendering the widget on every parent render.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    let widgetId: string | undefined;

    void loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language: locale === "zh" ? "zh-cn" : "en",
          theme: "light",
          callback: (token) => onTokenRef.current(token),
          "expired-callback": () => onTokenRef.current(null),
          "error-callback": () => onTokenRef.current(null),
          "timeout-callback": () => onTokenRef.current(null),
        });
      })
      .catch(() => {
        // Script failed to load — degrade by submitting without a token.
        if (!cancelled) onTokenRef.current(null);
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* widget already gone */
        }
      }
    };
  }, [siteKey, locale]);

  // Turnstile disabled: render nothing (form submits without a token).
  if (!siteKey) return null;

  return <div ref={containerRef} className={className} aria-live="polite" />;
}
